import { YourAddressProps } from "./YourAddressProps";
import { getCurrentStateValues, useDonationFormStore } from "@/stores";
import type { AddressFields } from "@/types/Address";
import { useTranslation } from "@/hooks/useTranslation";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { VerificationWarning } from "../VerificationWarning";
import { useAddressVerification } from "@/hooks/useAddressVerification";
import classes from "./YourAddress.module.scss";
import { clsx } from "clsx";
import { useImperativeHandle, useRef, useState } from "react";
import type { ImperativeHandle } from "@/types/Form";
import type { AddressFieldsPayload } from "@/types/Loqate";

function toAddressPayload(address: AddressFields): AddressFieldsPayload {
  return {
    address_line_1: address["address-line-1"] ?? "",
    address_line_2: address["address-line-2"] ?? "",
    city: address.city ?? "",
    region: address.region ?? "",
    postal_code: address["postal-code"] ?? "",
    country: address.country ?? "",
  };
}

function toAddressFields(suggestion: AddressFieldsPayload): AddressFields {
  return {
    "address-line-1": suggestion.address_line_1,
    "address-line-2": suggestion.address_line_2,
    city: suggestion.city,
    region: suggestion.region,
    "postal-code": suggestion.postal_code,
    country: suggestion.country,
  };
}

function formatAddressSuggestion(suggestion: AddressFieldsPayload): string {
  return [
    suggestion.address_line_1,
    suggestion.address_line_2,
    suggestion.city,
    suggestion.region,
    suggestion.postal_code,
    suggestion.country,
  ]
    .filter(Boolean)
    .join(", ");
}

export function YourAddress(props: YourAddressProps) {
  const { className, config, ref } = props;

  const isManualAddress = useDonationFormStore((s) => s.isManualAddress);
  const setFieldValue = useDonationFormStore((s) => s.setFieldValue);
  const setAddressVerificationStatus = useDonationFormStore(
    (s) => s.setAddressVerificationStatus,
  );

  const t = useTranslation("your_address");
  const tc = useTranslation("_common");

  const groupRef = useRef<ImperativeHandle>(null);
  // The address Loqate check runs as a gate on Continue (via validate()), not on
  // blur, so it can't race the Continue click. The last acknowledged address
  // (verified, corrected, or kept by the donor) is recorded so re-clicking
  // Continue on an unchanged address advances instead of re-warning.
  const acknowledgedRef = useRef<string | null>(null);
  const [countryMissing, setCountryMissing] = useState(false);
  const {
    issue: addressIssue,
    checkAddress,
    clearIssue,
  } = useAddressVerification();

  // Put Loqate's address into the form and acknowledge it so the next Continue
  // advances without re-checking. Used both when the donor accepts a "did you
  // mean" and when the backend applied a small correction on its own.
  const applySuggestion = (
    suggestion: AddressFieldsPayload,
    verificationStatus: "verified" | "corrected",
  ) => {
    const fields = toAddressFields(suggestion);
    setFieldValue("address", fields);
    setAddressVerificationStatus(verificationStatus);
    acknowledgedRef.current = JSON.stringify(toAddressPayload(fields));
  };

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const fieldResult = (await groupRef.current?.validate()) ?? {
        isValid: true,
      };
      if (!fieldResult.isValid) {
        return fieldResult;
      }

      const payload = toAddressPayload(getCurrentStateValues().address);
      const key = JSON.stringify(payload);
      const hasStreetAndPostcode = Boolean(
        payload.address_line_1 && payload.city && payload.postal_code,
      );

      // Loqate can't verify without a country, so require one whenever there is
      // an address to check — independent of the field's own required toggle.
      if (hasStreetAndPostcode && !payload.country) {
        setCountryMissing(true);
        return { isValid: false };
      }
      setCountryMissing(false);

      const complete = hasStreetAndPostcode && Boolean(payload.country);
      if (!complete || key === acknowledgedRef.current) {
        return { isValid: true };
      }

      const result = await checkAddress(payload);
      if (result?.status === "unverified") {
        return { isValid: false }; // the hook raised the warning; hold here
      }

      if (result?.status === "corrected" && result.suggestion) {
        applySuggestion(result.suggestion, "corrected");
        return { isValid: true };
      }

      if (result?.status === "verified" || result?.status === "corrected") {
        setAddressVerificationStatus("verified");
      }
      acknowledgedRef.current = key; // verified or unchecked → advance
      return { isValid: true };
    },
  }));

  const addressConfig = config["your-address-fields"];
  const footerTitle = t(config["your-address-footer-title"].i18n);
  const footerBody = t(config["your-address-footer-body"].i18n);

  return (
    <ValidationGroupProvider name="address" ref={groupRef}>
      <div className={clsx(classes.root, className)}>
        <AddressAutocomplete
          key={isManualAddress ? "manual" : "search"}
          label={t("label") as string}
          name="address"
          backButtonLabel={t("back-to-search") as string}
          helpText={t("address-autocomplete-help-text") as string}
          fieldsConfig={{
            "address-line-1": {
              label: t(addressConfig["address-line-1"].label.i18n) as string,
              serverErrorPath: "your_address.address_line_1",
              name: "address-line-1",
              isRequired: addressConfig["address-line-1"]["is-required"],
            },
            "address-line-2": {
              label: t(addressConfig["address-line-2"].label.i18n) as string,
              serverErrorPath: "your_address.address_line_2",
              name: "address-line-2",
              isRequired: addressConfig["address-line-2"]["is-required"],
            },
            city: {
              label: t(addressConfig.city.label.i18n) as string,
              serverErrorPath: "your_address.city",
              name: "city",
              isRequired: addressConfig.city["is-required"],
            },
            "postal-code": {
              label: t(addressConfig["postal-code"].label.i18n) as string,
              serverErrorPath: "your_address.postal_code",
              name: "postal-code",
              isRequired: addressConfig["postal-code"]["is-required"],
            },
            region: {
              label: t(addressConfig.region.label.i18n) as string,
              serverErrorPath: "your_address.region",
              name: "region",
              isRequired: addressConfig.region["is-required"],
            },
            country: {
              label: t(addressConfig.country.label.i18n) as string,
              serverErrorPath: "your_address.country",
              name: "country",
              isRequired: addressConfig.country["is-required"],
            },
          }}
          initialScreen={isManualAddress ? "form" : "search"}
          footerForm={
            <div className={classes.formatter}>
              <div className={classes.title}>{footerTitle}</div>
              <div>{footerBody}</div>
            </div>
          }
        />

        {countryMissing && (
          <p role="alert" className={classes.countryError}>
            {tc("address-country-required") as string}
          </p>
        )}

        {addressIssue && (
          <VerificationWarning
            message={
              addressIssue.suggestion
                ? `${tc("address-did-you-mean")} ${formatAddressSuggestion(addressIssue.suggestion)}`
                : (tc("address-unverified") as string)
            }
            suggestion={
              addressIssue.suggestion
                ? {
                    label: tc("address-use-suggestion") as string,
                    onAccept: () => {
                      applySuggestion(addressIssue.suggestion!, "verified");
                      clearIssue();
                    },
                  }
                : null
            }
            onDismiss={() => {
              setAddressVerificationStatus("unverified_proceeded");
              acknowledgedRef.current = JSON.stringify(
                toAddressPayload(getCurrentStateValues().address),
              );
              clearIssue();
            }}
            dismissLabel={tc("address-keep-mine") as string}
          />
        )}
      </div>
    </ValidationGroupProvider>
  );
}
